import { inject } from '@angular/core';
import { CanActivateFn, ActivatedRouteSnapshot } from '@angular/router';
import { MembershipService } from '../../services/membership/membership.service';
import { AuthService } from '../../services/auth';
import { of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

/**
 * Map of routes to their required feature names
 */
const ROUTE_FEATURE_MAP: Record<string, string> = {
  '/promos': 'Promociones y campañas',
  '/inventory': 'Gestión de productos e inventario',
  '/sales': 'Ventas y reportes integrados',
};

/**
 * Feature guard that checks if user has access to a specific feature
 * If user doesn't have access, they stay on the current page (no redirect)
 */
export const featureGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const membershipService = inject(MembershipService);
  const authService = inject(AuthService);

  // Skip feature validation for system administrators
  if (authService.isSystemAdmin()) {
    return of(true);
  }

  // Get the route path - try multiple formats
  const routePath = route.routeConfig?.path || '';
  const fullPath = route.url.length > 0
    ? '/' + route.url.map(segment => segment.path).join('/')
    : '/' + routePath;

  // Find the required feature for this route
  // Try full path first, then route path with /, then route path without /
  const requiredFeature = ROUTE_FEATURE_MAP[fullPath]
    || ROUTE_FEATURE_MAP['/' + routePath]
    || ROUTE_FEATURE_MAP[routePath];

  // If no feature is required for this route, allow access
  if (!requiredFeature) {
    return of(true);
  }

  // Check if user has the feature (try sync first, then async)
  const hasFeatureSync = membershipService.hasFeature(requiredFeature);
  if (hasFeatureSync) {
    return of(true);
  }

  // If not in cache, check async
  return membershipService.hasFeatureAsync(requiredFeature).pipe(
    map(hasFeature => {
      if (!hasFeature) {
        // Don't redirect, just prevent navigation
        // The user stays on their current page
        return false;
      }
      return true;
    }),
    catchError(() => {
      // On error, deny access (user stays on current page)
      return of(false);
    })
  );
};

